/**
 * SportOnboard — replaces the old 5-step onboarding (intention → summary).
 * Slide 0: "Alright! Are you ready?" — Knight Rider gold/blue border button.
 * Slide 1: 3-2-1 countdown, each number fadeUp then exits.
 * onDone() — fires after the "1" animates out.
 */
import React, { useState, useEffect } from 'react';
import { _T, CSS } from '../../shared/theme';

/* ── CSS ──────────────────────────────────────────────────────────────────── */
const SO_CSS = `
/* Knight-rider rotating border */
@property --kr-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
@keyframes kr-spin      { to { --kr-angle: 360deg; } }
@keyframes so-glow-bob  { 0%,100%{opacity:.35} 50%{opacity:.8} }
@keyframes so-rise      { from{opacity:0;transform:translateY(26px)} to{opacity:1;transform:translateY(0)} }
@keyframes so-count     {
  0%   { opacity:0; transform:translateY(50px) scale(.55); }
  28%  { opacity:1; transform:translateY(0)    scale(1.08); }
  65%  { opacity:1; transform:translateY(0)    scale(1);   }
  100% { opacity:0; transform:translateY(-38px) scale(.9); }
}
@keyframes so-btn-glow  { 0%,100%{filter:brightness(1)} 50%{filter:brightness(1.18)} }
@keyframes so-ripple    {
  from { transform:scale(0); opacity:.6; }
  to   { transform:scale(4); opacity:0;  }
}
@keyframes so-slide-out { to { opacity:0; transform:translateY(-30px); } }

/* Knight rider border wrapper */
.kr-wrap {
  display: inline-block;
  padding: 2.5px;
  border-radius: 20px;
  background: conic-gradient(
    from var(--kr-angle),
    transparent 60%,
    #C8A84B 74%, #93c5fd 80%,
    #C8A84B 86%, transparent 100%
  );
  animation: kr-spin 2.2s linear infinite;
}
.kr-btn {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0b1929 0%, #162438 100%);
  border: none;
  border-radius: 17px;
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  padding: 17px 44px;
  cursor: pointer;
  letter-spacing: .2px;
  font-family: 'Inter', system-ui, sans-serif;
  transition: transform .14s, filter .14s;
  animation: so-btn-glow 2.5s ease-in-out infinite;
  white-space: nowrap;
}
.kr-btn:hover  { transform:scale(1.04); filter:brightness(1.12); }
.kr-btn:active { transform:scale(.96);  filter:brightness(.9);   }
.kr-ripple {
  position:absolute;
  border-radius:50%;
  width:60px; height:60px;
  margin:-30px 0 0 -30px;
  background:rgba(200,168,75,.45);
  pointer-events:none;
  animation: so-ripple .55s ease-out forwards;
}
`;

function delay(ms) { return new Promise(r => setTimeout(r, ms)); }

/* ── Component ────────────────────────────────────────────────────────────── */
export default function SportOnboard({ lang, onDone }) {
  const nl = lang === 'nl';
  const [slide,    setSlide]    = useState(0);   // 0 = ready, 1 = countdown
  const [count,    setCount]    = useState(3);
  const [countKey, setCountKey] = useState(0);   // remount key → re-triggers CSS animation
  const [ripples,  setRipples]  = useState([]);  // [{id,x,y}]
  const [exiting,  setExiting]  = useState(false);

  /* Countdown sequence */
  useEffect(() => {
    if (slide !== 1) return;
    let cancelled = false;
    (async () => {
      for (const n of [3, 2, 1]) {
        if (cancelled) return;
        setCount(n);
        setCountKey(k => k + 1);
        await delay(900);
      }
      if (!cancelled) onDone();
    })();
    return () => { cancelled = true; };
  }, [slide]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReady = (e) => {
    /* Ripple effect on button click */
    const rect = e.currentTarget.getBoundingClientRect();
    const id   = Date.now();
    setRipples(r => [...r, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples(r => r.filter(x => x.id !== id)), 600);

    /* Slide-out then switch */
    setExiting(true);
    setTimeout(() => { setExiting(false); setSlide(1); }, 380);
  };

  /* ── Render ────────────────────────────────────────────────────────────── */
  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg, ${_T.bg} 0%, #0c1a2e 100%)`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{CSS + SO_CSS}</style>

      {/* Ambient glows */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
        <div style={{
          position:'absolute', width:700, height:700, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(200,168,75,.11) 0%, transparent 70%)',
          top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          animation:'so-glow-bob 3.5s ease-in-out infinite',
        }}/>
        <div style={{
          position:'absolute', width:360, height:360, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(96,165,250,.07) 0%, transparent 70%)',
          top:'18%', right:'8%',
          animation:'so-glow-bob 4.8s 1.1s ease-in-out infinite',
        }}/>
        <div style={{
          position:'absolute', width:240, height:240, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(200,168,75,.06) 0%, transparent 70%)',
          bottom:'12%', left:'6%',
          animation:'so-glow-bob 5.2s 0.7s ease-in-out infinite',
        }}/>
      </div>

      {/* ── Slide 0: Ready ──────────────────────────────────────────────────── */}
      {slide === 0 && (
        <div style={{
          textAlign: 'center', padding: '0 32px', maxWidth: 400,
          animation: exiting
            ? 'so-slide-out .35s ease-in forwards'
            : 'so-rise .55s cubic-bezier(.34,1.56,.64,1) both',
        }}>
          {/* Logo */}
          <div style={{ marginBottom: 28, animation: 'so-glow-bob 2.8s ease-in-out infinite' }}>
            <img
              src="https://senselifting.com/3d_logo.png"
              alt="Sense Lifting"
              style={{ width: 90 }}
            />
          </div>

          {/* Badge */}
          <div style={{
            display:'inline-block',
            background:'rgba(200,168,75,.12)',
            border:'1px solid rgba(200,168,75,.3)',
            borderRadius:20, padding:'5px 16px',
            fontSize:11, fontWeight:700, color:'#C8A84B',
            letterSpacing:2, textTransform:'uppercase',
            marginBottom:20,
          }}>
            {nl ? '⚡ Sense Lifting Training' : '⚡ Sense Lifting Training'}
          </div>

          {/* Main title */}
          <div style={{
            fontSize: 40, fontWeight: 900, color: '#fff',
            letterSpacing: -1.2, marginBottom: 10, lineHeight: 1.05,
            fontFamily: 'Georgia, serif',
          }}>
            {nl ? 'Alright!' : 'Alright!'}
          </div>

          {/* Sub headline */}
          <div style={{
            fontSize: 20, fontWeight: 700,
            background: 'linear-gradient(90deg, #C8A84B, #93c5fd)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            marginBottom: 14, letterSpacing: 0.1, lineHeight: 1.25,
          }}>
            {nl
              ? 'Ben jij klaar om je Senses te boosten?'
              : 'Are you ready to boost your Senses?'}
          </div>

          {/* Description */}
          <div style={{
            fontSize: 13.5, color: 'rgba(255,255,255,.42)',
            marginBottom: 50, lineHeight: 1.7,
          }}>
            {nl
              ? 'Een korte maar krachtige intro — je gaat direct ervaren wat dit platform voor je doet.'
              : 'A short but powerful intro — you\'ll immediately experience what this platform does for you.'}
          </div>

          {/* Knight Rider CTA */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="kr-wrap">
              <button className="kr-btn" onClick={handleReady}>
                {nl ? '⚡ Klaar, Let\'s Go!' : '⚡ Ready, Let\'s Go!'}
                {ripples.map(r => (
                  <span key={r.id} className="kr-ripple" style={{ left: r.x, top: r.y }}/>
                ))}
              </button>
            </div>
          </div>

          {/* Subtle hint */}
          <div style={{
            marginTop: 22, fontSize: 11, color: 'rgba(255,255,255,.22)',
            letterSpacing: 1, textTransform: 'uppercase',
          }}>
            {nl ? 'Duurt minder dan 5 minuten' : 'Takes less than 5 minutes'}
          </div>
        </div>
      )}

      {/* ── Slide 1: Countdown ──────────────────────────────────────────────── */}
      {slide === 1 && (
        <div style={{ textAlign: 'center', userSelect: 'none' }}>
          <div key={countKey} className="so-count-anim" style={{
            fontSize: 160, fontWeight: 900, color: '#fff',
            fontFamily: 'Georgia, serif', lineHeight: 1,
            textShadow: `
              0 0 40px rgba(200,168,75,.7),
              0 0 100px rgba(200,168,75,.35),
              0 0 6px rgba(255,255,255,.9)
            `,
          }}>
            {count}
          </div>
          <div style={{
            fontSize: 13, color: 'rgba(255,255,255,.35)',
            marginTop: 28, letterSpacing: 3.5, textTransform: 'uppercase',
            fontWeight: 700,
          }}>
            {nl ? 'Klaar voor de start' : 'Get ready'}
          </div>
        </div>
      )}

      {/* Legal */}
      <div style={{
        position:'absolute', bottom:18, left:0, right:0,
        display:'flex', justifyContent:'center', gap:20,
      }}>
        <a href="/privacy" style={{ color:'#2a3a52', fontSize:11, textDecoration:'none' }}>Privacy Policy</a>
        <a href="/terms"   style={{ color:'#2a3a52', fontSize:11, textDecoration:'none' }}>Terms &amp; Conditions</a>
      </div>
    </div>
  );
}
